import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { isWebpFile } from "../../utils/fileValidation";
import { formatDate } from "../../utils/date";

const getRows = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

const Field = ({ label, value }) => (
  <div className="col-md-6">
    <div className="text-muted small">{label}</div>
    <div className="fw-semibold">{value || "-"}</div>
  </div>
);

const LabTechniciansList = () => {
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [viewData, setViewData] = useState(null);
  const [basicModal, setBasicModal] = useState(false);
  const [labModal, setLabModal] = useState(false);
  const [educationModal, setEducationModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteIds, setDeleteIds] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [basicForm, setBasicForm] = useState({
    fullName: "",
    mobileNumber: "",
    email: "",
    dob: "",
    age: "",
    alterMobileNumber: "",
    address1: "",
    address2: "",
    pincode: "",
  });
  const [labForm, setLabForm] = useState({
    agentId: "",
    labName: "",
    licenceNumber: "",
    address1: "",
    address2: "",
    landmark: "",
    pincode: "",
  });
  const [educationForm, setEducationForm] = useState({
    agentId: "",
    higherEducation: "",
    degreeName: "",
    passingYear: "",
    images: null,
  });

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const loadTechnicians = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(
        `/admin/lab-technician?page=${pageNo}&limit=${limit}`,
        { method: "GET" }
      );
      setTechnicians(getRows(response.data));
      setTotalRecords(response?.data?.tr || 0);
    } catch (error) {
      setTechnicians([]);
      setTotalRecords(0);
      showSnack("Error fetching lab technicians", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTechnicians();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, limit]);

  const filteredTechnicians = useMemo(() => {
    if (!query.trim()) return technicians;
    const search = query.toLowerCase();
    return technicians.filter(
      (item) =>
        item.fullName?.toLowerCase().includes(search) ||
        item.email?.toLowerCase().includes(search) ||
        item.mobileNumber?.toLowerCase().includes(search) ||
        item.labName?.toLowerCase().includes(search) ||
        item.labId?.toLowerCase().includes(search)
    );
  }, [query, technicians]);
  const totalPages = Math.ceil(totalRecords / limit) || 1;
  const visibleIds = filteredTechnicians.map((item) => item._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const openDeleteModal = (ids) => {
    if (!ids.length) {
      showSnack("Select at least one lab technician", "error");
      return;
    }
    setDeleteIds(ids);
    setDeleteModal(true);
  };

  const deleteTechnicians = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician", {
        method: "DELETE",
        body: JSON.stringify({ ids: deleteIds }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(response?.data?.m || "Lab technicians deleted successfully");
        setSelectedIds((prev) => prev.filter((id) => !deleteIds.includes(id)));
        setDeleteModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error deleting lab technicians", "error");
      }
    } catch (error) {
      showSnack("Error deleting lab technicians", "error");
    } finally {
      setLoading(false);
      setDeleteIds([]);
    }
  };

  const openView = async (id) => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/lab-technician/${id}`, {
        method: "GET",
      });
      setViewData(response?.data?.r || response?.data?.data || {});
    } catch (error) {
      showSnack("Error loading technician details", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateTechnician = async (id, action) => {
    const url =
      action === "approve"
        ? `/admin/lab-technician/approve/${id}`
        : `/admin/lab-technician/${id}`;
    setLoading(true);
    try {
      const response = await AuthInterceptor(url, { method: "PATCH" });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(`Technician ${action === "approve" ? "approval" : "status"} updated`);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error updating technician", "error");
      }
    } catch (error) {
      showSnack("Error updating technician", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitBasic = async (event) => {
    event.preventDefault();
    const body = { ...basicForm, age: basicForm.age ? Number(basicForm.age) : "" };
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician/register-basic", {
        method: "POST",
        body: JSON.stringify(body),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Basic details saved successfully");
        setBasicModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error saving basic details", "error");
      }
    } catch (error) {
      showSnack("Error saving basic details", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitLab = async (event) => {
    event.preventDefault();
    if (!labForm.agentId) {
      showSnack("Select a technician from the list first", "error");
      return;
    }
    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician/register-lab", {
        method: "POST",
        body: JSON.stringify(labForm),
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Laboratory details saved successfully");
        setLabModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error saving laboratory details", "error");
      }
    } catch (error) {
      showSnack("Error saving laboratory details", "error");
    } finally {
      setLoading(false);
    }
  };

  const submitEducation = async (event) => {
    event.preventDefault();
    if (!educationForm.agentId) {
      showSnack("Select a technician from the list first", "error");
      return;
    }
    const formData = new FormData();
    formData.append("agentId", educationForm.agentId);
    formData.append("higherEducation", educationForm.higherEducation);
    formData.append("degreeName", educationForm.degreeName);
    formData.append("passingYear", educationForm.passingYear);
    const images = Array.from(educationForm.images || []);
    if (images.some((file) => !isWebpFile(file))) {
      showSnack("Only WebP, PNG, JPG, and JPEG image files are allowed", "error");
      return;
    }
    images.forEach((file) => {
      formData.append("images", file);
    });

    setLoading(true);
    try {
      const response = await AuthInterceptor("/admin/lab-technician/register-education", {
        method: "POST",
        body: formData,
      });
      if ([200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status)) {
        showSnack("Education details saved successfully");
        setEducationModal(false);
        loadTechnicians();
      } else {
        showSnack(response?.data?.m || "Error saving education details", "error");
      }
    } catch (error) {
      showSnack("Error saving education details", "error");
    } finally {
      setLoading(false);
    }
  };

  const openForAgent = (row, modal) => {
    const agentId = row?._id || "";
    if (modal === "basic") {
      setBasicModal(true);
    }
    if (modal === "lab") {
      setLabForm((prev) => ({ ...prev, agentId }));
      setLabModal(true);
    }
    if (modal === "education") {
      setEducationForm((prev) => ({ ...prev, agentId }));
      setEducationModal(true);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h2 className="mb-0">Lab Technicians</h2>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm w-auto"
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            <button className="btn btn-outline-custom btn-sm" onClick={() => openForAgent(null, "basic")}>
              Register Basic
            </button>
          </div>
        </div>

        <div className="input-group mb-3">
          <input className="form-control" placeholder="Search lab technicians..." value={query} onChange={(e) => setQuery(e.target.value)} />
          <button className="btn btn-outline-secondary">Search</button>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">{selectedIds.length} selected</span>
          <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteModal(selectedIds)}>
            Delete
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all lab technicians on this page"
                  />
                </th>
                <th>Sr.</th>
                <th>Lab ID</th>
                <th>Name</th>
                <th>Lab Name</th>
                <th>Mobile</th>
                <th>Total Orders</th>
                <th>Status</th>
                <th>Approval</th>
                <th style={{ width: "310px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTechnicians.length > 0 ? (
                filteredTechnicians.map((item, index) => (
                  <tr key={item._id || index}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(item._id)}
                        onChange={() => toggleRowSelection(item._id)}
                        aria-label={`Select ${item.fullName || "lab technician"}`}
                      />
                    </td>
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    <td className="fw-semibold">{item.labId || "-"}</td>
                    <td>{item.fullName || item.name || "-"}</td>
                    <td>{item.labName || item.lab?.labName || "-"}</td>
                    <td>{item.mobileNumber || "-"}</td>
                    <td>{item.totalOrders ?? 0}</td>
                    <td>
                      <span
                        className={`badge ${item.isActive ? "bg-success" : "bg-secondary"}`}
                        role="button"
                        onClick={() => updateTechnician(item._id, "status")}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${item.isApproved ? "bg-success" : "bg-warning text-dark"}`}>
                        {item.isApproved ? "Completed" : "Pending"}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-sm btn-outline-info" onClick={() => openView(item._id)}>
                          <CgEye size={16} />
                        </button>
                        <button className="btn btn-sm btn-outline-secondary" onClick={() => updateTechnician(item._id, "status")}>
                          Status
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => updateTechnician(item._id, "approve")}>
                          Approve
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openForAgent(item, "lab")}>
                          Lab
                        </button>
                        <button className="btn btn-sm btn-outline-custom" onClick={() => openForAgent(item, "education")}>
                          Education
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => openDeleteModal([item._id])}>
                          <CgTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
                ) : (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-4">
                    No lab technicians found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <button className="btn btn-outline-custom btn-sm" disabled={pageNo === 1} onClick={() => setPageNo((page) => page - 1)}>
            Prev
          </button>
          <span>Page {pageNo} of {totalPages}</span>
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo >= totalPages}
            onClick={() => setPageNo((page) => Math.min(page + 1, totalPages))}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={Boolean(viewData)} onHide={() => setViewData(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Lab Technician Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewData && (
            <div className="d-grid gap-4">
              <section>
                <div className="fw-semibold mb-2">Basic Details</div>
                <div className="row g-3">
                  <Field label="Lab ID" value={viewData.labId} />
                  <Field label="Name" value={viewData.fullName} />
                  <Field label="Mobile" value={viewData.mobileNumber} />
                  <Field label="Email" value={viewData.email} />
                  <Field label="Total Orders" value={viewData.totalOrders ?? 0} />
                  <Field label="Created" value={formatDate(viewData.createdAt)} />
                  <Field label="Status" value={viewData.isActive ? "Active" : "Inactive"} />
                  <Field label="Approval" value={viewData.isApproved ? "Completed" : "Pending"} />
                </div>
              </section>

              <section>
                <div className="fw-semibold mb-2">Lab Details</div>
                <div className="row g-3">
                  <Field label="Lab Name" value={viewData.lab?.labName} />
                  <Field label="Licence Number" value={viewData.lab?.licenceNumber} />
                  <Field label="Address" value={[viewData.lab?.address1, viewData.lab?.address2].filter(Boolean).join(", ")} />
                  <Field label="Landmark" value={viewData.lab?.landmark} />
                  <Field label="Pincode" value={viewData.lab?.pincode} />
                </div>
              </section>

              <section>
                <div className="fw-semibold mb-2">Education Details</div>
                <div className="row g-3">
                  <Field label="Higher Education" value={viewData.educationDetails?.higherEducation} />
                  <Field label="Degree Name" value={viewData.educationDetails?.degreeName} />
                  <Field label="Passing Year" value={viewData.educationDetails?.passingYear} />
                  <Field label="Label" value={viewData.labEducationLabel} />
                </div>
              </section>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={basicModal} onHide={() => setBasicModal(false)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Register Basic</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitBasic}>
            <div className="row">
              {Object.keys(basicForm).map((name) => (
                <div className="col-md-6 mb-3" key={name}>
                  <label className="form-label">{name}</label>
                  <input className="form-control" type={name === "dob" ? "date" : "text"} value={basicForm[name]} onChange={(e) => setBasicForm((prev) => ({ ...prev, [name]: e.target.value }))} />
                </div>
              ))}
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setBasicModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={labModal} onHide={() => setLabModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Register Laboratory</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitLab}>
            {Object.keys(labForm).filter((name) => name !== "agentId").map((name) => (
              <div className="mb-3" key={name}>
                <label className="form-label">{name}</label>
                <input className="form-control" value={labForm[name]} onChange={(e) => setLabForm((prev) => ({ ...prev, [name]: e.target.value }))} />
              </div>
            ))}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setLabModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={educationModal} onHide={() => setEducationModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Register Education</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={submitEducation}>
            {["higherEducation", "degreeName", "passingYear"].map((name) => (
              <div className="mb-3" key={name}>
                <label className="form-label">{name}</label>
                <input className="form-control" value={educationForm[name]} onChange={(e) => setEducationForm((prev) => ({ ...prev, [name]: e.target.value }))} />
              </div>
            ))}
            <div className="mb-3">
              <label className="form-label">images</label>
              <input className="form-control" type="file" accept="image/webp,image/png,image/jpeg" multiple onChange={(e) => setEducationForm((prev) => ({ ...prev, images: e.target.files }))} />
            </div>
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEducationModal(false)}>Cancel</button>
              <button type="submit" className="btn btn-outline-custom">Save</button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={deleteModal} onHide={() => setDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Lab Technicians</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            This action can not be reversed. Are you sure you want to delete{" "}
            <strong>{deleteIds.length}</strong> lab technician{deleteIds.length > 1 ? "s" : ""}?
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button type="button" className="btn btn-outline-secondary" onClick={() => setDeleteModal(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-danger" onClick={deleteTechnicians}>
              Delete
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default LabTechniciansList;
